import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const DEFAULT_LAYER = {
  inset: false,
  x: 0,
  y: 8,
  blur: 24,
  spread: -4,
  color: "#1e293b",
  alpha: 0.18,
};

const PRESETS = [
  {
    key: "subtle",
    layers: [
      { inset: false, x: 0, y: 1, blur: 2, spread: 0, color: "#0f172a", alpha: 0.08 },
      { inset: false, x: 0, y: 1, blur: 3, spread: 0, color: "#0f172a", alpha: 0.06 },
    ],
  },
  {
    key: "card",
    layers: [
      { inset: false, x: 0, y: 4, blur: 6, spread: -1, color: "#0f172a", alpha: 0.1 },
      { inset: false, x: 0, y: 2, blur: 4, spread: -2, color: "#0f172a", alpha: 0.1 },
    ],
  },
  {
    key: "popover",
    layers: [
      { inset: false, x: 0, y: 10, blur: 15, spread: -3, color: "#0f172a", alpha: 0.1 },
      { inset: false, x: 0, y: 4, blur: 6, spread: -4, color: "#0f172a", alpha: 0.1 },
    ],
  },
  {
    key: "floating",
    layers: [
      { inset: false, x: 0, y: 25, blur: 50, spread: -12, color: "#0f172a", alpha: 0.25 },
    ],
  },
  {
    key: "inner",
    layers: [
      { inset: true, x: 0, y: 2, blur: 4, spread: 0, color: "#0f172a", alpha: 0.15 },
    ],
  },
  {
    key: "neon",
    layers: [
      { inset: false, x: 0, y: 0, blur: 12, spread: 0, color: "#38bdf8", alpha: 0.7 },
      { inset: false, x: 0, y: 0, blur: 32, spread: 4, color: "#0ea5e9", alpha: 0.45 },
    ],
  },
];

const clampInt = (raw, min, max) => {
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
};

const clampAlpha = (raw) => {
  const n = Number.parseFloat(raw);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
};

const sanitizeHex = (raw) => {
  if (!raw) return "#000000";
  const v = raw.trim();
  if (/^#([0-9a-fA-F]{6})$/.test(v)) return v.toLowerCase();
  if (/^#([0-9a-fA-F]{3})$/.test(v)) {
    const r = v[1], g = v[2], b = v[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return null;
};

const hexToRgba = (hex, alpha) => {
  const clean = sanitizeHex(hex) || "#000000";
  const r = Number.parseInt(clean.slice(1, 3), 16);
  const g = Number.parseInt(clean.slice(3, 5), 16);
  const b = Number.parseInt(clean.slice(5, 7), 16);
  const a = Math.round(alpha * 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const layerToCss = (layer) => {
  const insetPart = layer.inset ? "inset " : "";
  return `${insetPart}${layer.x}px ${layer.y}px ${layer.blur}px ${layer.spread}px ${hexToRgba(layer.color, layer.alpha)}`;
};

const buildCss = (layers) => layers.map(layerToCss).join(",\n            ");

const BG_OPTIONS = [
  { key: "light", value: "#f8fafc" },
  { key: "dark", value: "#0f172a" },
  { key: "checker", value: "checker" },
];

const CHECKER_BG =
  "repeating-conic-gradient(#e2e8f0 0% 25%, #f8fafc 0% 50%) 50% / 24px 24px";

const BoxShadowGenerator = () => {
  const { t } = useTranslation();
  const [layers, setLayers] = useState([{ ...DEFAULT_LAYER }]);
  const [bg, setBg] = useState("light");
  const [boxColor, setBoxColor] = useState("#ffffff");
  const [radius, setRadius] = useState(12);

  const css = useMemo(() => buildCss(layers), [layers]);
  const fullCss = useMemo(
    () => `box-shadow: ${css};`,
    [css],
  );

  const updateLayer = (idx, patch) => {
    setLayers((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    );
  };

  const addLayer = () => {
    if (layers.length >= 8) {
      toast.error(t("box_shadow_max_layers", { max: 8 }));
      return;
    }
    setLayers((prev) => [...prev, { ...DEFAULT_LAYER }]);
  };

  const removeLayer = (idx) => {
    setLayers((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== idx),
    );
  };

  const applyPreset = (preset) => {
    setLayers(preset.layers.map((l) => ({ ...l })));
  };

  const reset = () => {
    setLayers([{ ...DEFAULT_LAYER }]);
    setBg("light");
    setBoxColor("#ffffff");
    setRadius(12);
  };

  const copy = async (text, msgKey = "copy_success") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t(msgKey));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const bgStyle = (() => {
    const opt = BG_OPTIONS.find((o) => o.key === bg) || BG_OPTIONS[0];
    if (opt.value === "checker") return { background: CHECKER_BG };
    return { background: opt.value };
  })();

  const previewBoxStyle = {
    background: boxColor,
    borderRadius: `${radius}px`,
    boxShadow: layers.map(layerToCss).join(", "),
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-6xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("box_shadow_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("box_shadow_intro")}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("box_shadow_presets_label")}
              </span>
              {PRESETS.map((p) => (
                <StandardButton
                  key={p.key}
                  onClick={() => applyPreset(p)}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t(`box_shadow_preset_${p.key}`)}
                </StandardButton>
              ))}
              <StandardButton
                onClick={reset}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("box_shadow_reset")}
              </StandardButton>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="flex flex-col gap-4">
                {layers.map((layer, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-3 bg-white/60"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">
                        {t("box_shadow_layer_n", { n: idx + 1 })}
                      </h3>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={layer.inset}
                            onChange={(e) =>
                              updateLayer(idx, { inset: e.target.checked })
                            }
                            aria-label={t("box_shadow_inset")}
                          />
                          {t("box_shadow_inset")}
                        </label>
                        {layers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLayer(idx)}
                            className="text-xs text-rose-600 hover:underline"
                            aria-label={t("box_shadow_remove_layer", {
                              n: idx + 1,
                            })}
                          >
                            {t("box_shadow_remove")}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <NumberSlider
                        label={t("box_shadow_offset_x")}
                        value={layer.x}
                        min={-100}
                        max={100}
                        onChange={(v) => updateLayer(idx, { x: v })}
                        unit="px"
                      />
                      <NumberSlider
                        label={t("box_shadow_offset_y")}
                        value={layer.y}
                        min={-100}
                        max={100}
                        onChange={(v) => updateLayer(idx, { y: v })}
                        unit="px"
                      />
                      <NumberSlider
                        label={t("box_shadow_blur")}
                        value={layer.blur}
                        min={0}
                        max={200}
                        onChange={(v) => updateLayer(idx, { blur: v })}
                        unit="px"
                      />
                      <NumberSlider
                        label={t("box_shadow_spread")}
                        value={layer.spread}
                        min={-100}
                        max={100}
                        onChange={(v) => updateLayer(idx, { spread: v })}
                        unit="px"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor={`bs-color-${idx}`}
                          className="text-xs font-medium text-gray-600"
                        >
                          {t("box_shadow_color")}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            id={`bs-color-${idx}`}
                            type="color"
                            value={sanitizeHex(layer.color) || "#000000"}
                            onChange={(e) =>
                              updateLayer(idx, { color: e.target.value })
                            }
                            className="h-9 w-12 border border-gray-300 rounded cursor-pointer"
                            aria-label={t("box_shadow_color")}
                          />
                          <input
                            type="text"
                            value={layer.color}
                            onChange={(e) =>
                              updateLayer(idx, { color: e.target.value })
                            }
                            onBlur={(e) => {
                              const safe = sanitizeHex(e.target.value);
                              if (safe) updateLayer(idx, { color: safe });
                            }}
                            className="flex-1 min-w-0 px-2 py-1 text-sm font-mono border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                            aria-label={t("box_shadow_color_hex")}
                          />
                        </div>
                      </div>
                      <NumberSlider
                        label={t("box_shadow_opacity")}
                        value={layer.alpha}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(v) => updateLayer(idx, { alpha: v })}
                        isFloat
                      />
                    </div>
                  </div>
                ))}

                <StandardButton
                  onClick={addLayer}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={layers.length >= 8}
                >
                  {t("box_shadow_add_layer")}
                </StandardButton>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">
                      {t("box_shadow_bg_label")}
                    </span>
                    {BG_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setBg(opt.key)}
                        aria-pressed={bg === opt.key}
                        className={`px-2 py-1 text-xs rounded border ${
                          bg === opt.key
                            ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                            : "border-gray-300 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {t(`box_shadow_bg_${opt.key}`)}
                      </button>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    {t("box_shadow_box_color")}
                    <input
                      type="color"
                      value={sanitizeHex(boxColor) || "#ffffff"}
                      onChange={(e) => setBoxColor(e.target.value)}
                      className="h-7 w-9 border border-gray-300 rounded cursor-pointer"
                      aria-label={t("box_shadow_box_color")}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    {t("box_shadow_radius")}
                    <input
                      type="number"
                      min={0}
                      max={200}
                      value={radius}
                      onChange={(e) =>
                        setRadius(clampInt(e.target.value, 0, 200))
                      }
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                      aria-label={t("box_shadow_radius")}
                    />
                    px
                  </label>
                </div>

                <div
                  className="rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden"
                  style={{
                    minHeight: 260,
                    padding: 32,
                    ...bgStyle,
                  }}
                  aria-label={t("box_shadow_preview_label")}
                >
                  <div
                    style={{
                      width: 160,
                      height: 160,
                      ...previewBoxStyle,
                    }}
                  />
                </div>

                <div className="flex items-center justify-between mt-2">
                  <h2 className="text-sm font-semibold text-gray-700">
                    {t("box_shadow_css_label")}
                  </h2>
                  <StandardButton
                    onClick={() => copy(fullCss)}
                    type="primary"
                    size="small"
                  >
                    {t("box_shadow_copy_css")}
                  </StandardButton>
                </div>
                <pre
                  className="text-xs font-mono bg-gray-900 text-emerald-200 p-3 rounded-md overflow-x-auto whitespace-pre"
                  aria-label={t("box_shadow_css_label")}
                >
{`.element {
    box-shadow: ${css};
}`}
                </pre>
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("box_shadow_note_layers")}</p>
              <p>{t("box_shadow_note_inset")}</p>
              <p>{t("box_shadow_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

const NumberSlider = ({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  unit,
  isFloat = false,
}) => {
  const handle = (raw) => {
    if (isFloat) onChange(clampAlpha(raw));
    else onChange(clampInt(raw, min, max));
  };
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => handle(e.target.value)}
          className="flex-1 accent-indigo-600"
          aria-label={label}
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => handle(e.target.value)}
          className="w-16 px-1 py-0.5 text-sm text-right font-mono border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
          aria-label={label}
        />
        {unit && <span className="text-xs text-gray-500 w-6">{unit}</span>}
      </div>
    </div>
  );
};

export default BoxShadowGenerator;
